<template>
    <div class="credit-card-form" :class="{ 'is-complete': is_complete }">
        <div v-if="!is_complete" class="form-content">
            <div class="form-row">
                <label class="form-label">Card Number</label>
                <div class="input-wrapper">
                    <input
                        ref="cardNumber"
                        v-model="card_number_display"
                        type="text"
                        class="form-input"
                        placeholder="1234 5678 9012 3456"
                        maxlength="19"
                        data-1p-ignore
                    />
                    <v-icon v-if="card_number_filled" class="autofill-check" color="success" x-small>mdi-check-circle</v-icon>
                </div>
            </div>

            <div class="form-row-group">
                <div class="form-row half">
                    <label class="form-label">Expiration</label>
                    <div class="input-wrapper">
                        <input
                            ref="expiration"
                            v-model="expiration_display"
                            type="text"
                            class="form-input"
                            placeholder="MM/YY"
                            maxlength="5"
                            data-1p-ignore
                        />
                        <v-icon v-if="expiration_filled" class="autofill-check" color="success" x-small>mdi-check-circle</v-icon>
                    </div>
                </div>

                <div class="form-row half">
                    <label class="form-label">CVV</label>
                    <div class="input-wrapper">
                        <input
                            ref="cvv"
                            v-model="cvv_display"
                            type="text"
                            class="form-input"
                            placeholder="123"
                            maxlength="3"
                            data-1p-ignore
                        />
                        <v-icon v-if="cvv_filled" class="autofill-check" color="success" x-small>mdi-check-circle</v-icon>
                    </div>
                </div>
            </div>

            <button
                class="pay-button"
                :class="{ 'is-processing': is_processing }"
                :disabled="!all_fields_filled || is_processing"
                @click="handlePay"
            >
                <span v-if="is_processing" class="button-content">
                    <span class="processing-spinner"></span>
                    <span>Processing...</span>
                </span>
                <span v-else class="button-content">
                    Pay {{ amount }}
                </span>
            </button>

            <div class="or-separator">
                <span class="or-line"></span>
                <span class="or-text">OR</span>
                <span class="or-line"></span>
            </div>

            <button
                class="apple-pay-button"
                :disabled="all_fields_filled"
                @click="handleApplePayClick"
            >
                <span class="button-content">
                    <svg class="apple-logo" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                    </svg>
                    <span>Pay</span>
                </span>
            </button>
        </div>

        <div v-else class="complete-content">
            <v-icon color="success" small>mdi-check-circle</v-icon>
            <span>Payment Complete</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockCreditCardForm',

    props: {
        amount: {
            type: String,
            default: ''
        },
        autofill_card: {
            type: Object,
            default: () => ({
                number: '4532 8912 3456 7890',
                expiration: '08/27',
                cvv: '412'
            })
        },
        autofill_delay: {
            type: Number,
            default: 300
        },
        processing_delay: {
            type: Number,
            default: 1500
        }
    },

    data() {
        return {
            card_number_display: '',
            expiration_display: '',
            cvv_display: '',
            card_number_filled: false,
            expiration_filled: false,
            cvv_filled: false,
            is_processing: false,
            is_complete: false
        };
    },

    computed: {
        all_fields_filled() {
            return this.card_number_display.length >= 15 &&
                   this.expiration_display.length >= 4 &&
                   this.cvv_display.length >= 3;
        }
    },

    methods: {
        handleApplePayClick() {
            if (this.all_fields_filled) return;

            const lastFour = this.autofill_card.number.slice(-4);
            this.card_number_display = `•••• •••• •••• ${lastFour}`;
            this.card_number_filled = true;

            this.expiration_display = this.autofill_card.expiration;
            this.expiration_filled = true;

            this.cvv_display = this.autofill_card.cvv;
            this.cvv_filled = true;

            this.$emit('autofill-complete');
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        handlePay() {
            if (this.is_processing || this.is_complete || !this.all_fields_filled) return;

            this.is_processing = true;
            this.$emit('processing');

            setTimeout(() => {
                this.is_processing = false;
                this.is_complete = true;
                this.$emit('complete');
            }, this.processing_delay);
        },

        reset() {
            this.card_number_display = '';
            this.expiration_display = '';
            this.cvv_display = '';
            this.card_number_filled = false;
            this.expiration_filled = false;
            this.cvv_filled = false;
            this.is_processing = false;
            this.is_complete = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.credit-card-form {
    width: 100%;
}

.form {
    &-content {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
    }

    &-row {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        &.half {
            flex: 1;
        }
    }

    &-row-group {
        display: flex;
        gap: 0.75rem;
    }

    &-label {
        font-size: 0.7rem;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    &-input {
        width: 100%;
        padding: 0.625rem 0.75rem;
        padding-right: 1.75rem;
        border: 1px solid #ddd;
        border-radius: 0.375rem;
        font-size: 0.9rem;
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
        background: #fff;
        color: #333;
        transition: border-color 0.2s ease, background-color 0.2s ease;

        &:focus {
            outline: none;
            border-color: var(--brand-primary);
        }

        &::placeholder {
            color: #bbb;
            font-family: inherit;
        }
    }
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.autofill-check {
    position: absolute;
    right: 0.5rem;
    animation: pop-in 0.2s ease;
}

@keyframes pop-in {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.pay-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.875rem 1.5rem;
    margin-top: 0.5rem;
    background: var(--brand-primary);
    border: none;
    border-radius: 0.5rem;
    color: white;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 3rem;

    &:hover:not(:disabled) {
        background: var(--brand-primary-dark, #305060);
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        background: #ccc;
    }

    &.is-processing {
        background: var(--brand-primary);
    }
}

.button-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #fff;
}

.processing-spinner {
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.complete-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;
    background: #e8f5e9;
    border-radius: 0.5rem;
    color: #2e7d32;
    font-weight: 600;
}

.or {
    &-separator {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin: 0.5rem 0;
    }

    &-line {
        flex: 1;
        height: 1px;
        background: #ddd;
    }

    &-text {
        font-size: 0.75rem;
        font-weight: 600;
        color: #999;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
}

.apple-pay-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.875rem 1.5rem;
    background: #000;
    border: none;
    border-radius: 0.5rem;
    color: white;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 3rem;

    &:hover:not(:disabled) {
        background: #333;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        background: #999;
        cursor: not-allowed;
    }

    .apple-logo {
        width: 1.25rem;
        height: 1.25rem;
    }
}
</style>
